import { useEffect, useMemo, useState } from 'react';
import {
  type AlignmentEssayResponse,
  type ApiError,
  type BullshitFinding,
  type ClaimCorrectionRecord,
  claimCorrectionKey,
  generateAlignmentEssay,
  getLatestAlignmentEssay,
} from '../../lib/api';
import {
  essayLandedAfter,
  recoveredEssayNote,
  recoveredEssayResult,
} from '../../lib/alignmentEssayRecovery';
import { buildPrintDocumentTitle } from '../../lib/printDocumentTitle';
import { printAlignmentEssay } from '../../lib/alignmentEssayToPrint';
import { type WordGuidanceMatch, deriveBudgetFromMatch, detectWordGuidanceAcross } from '../../lib/detectWordGuidance';
import { Card } from '../Card';
import { ClaimCorrectionControl } from '../ClaimCorrectionControl';
import { VoiceTextarea } from '../VoiceTextarea';
import clsx from '../clsx';
import { DriveSaveButton } from './DriveSaveButton';
import styles from './AlignmentEssayPanel.module.css';

// =============================================================================
// AlignmentEssayPanel — "Why {company}?" (docs/design/alignment-essay-
// module-2026-07-05.md + Jason's build-go additions). A third tailored
// artifact beside résumé + cover letter, mounted on JobDetail below the
// tailor/résumé cards. Mirrors JobResumePanel.tsx's OWN shape: a
// self-contained Card, its own honest idle/loading/error/blocked states, a
// Print/Save-as-PDF affordance — but adds the word-count SELECTOR (preset
// chips, no long form) and the react-to-refine loop the brief calls for.
//
// NO-LONG-FORMS LAW: the word-count control is FOUR tappable presets at most
// (Detected / Short / Standard / Custom — Detected only ever appears when the
// job's own text states one), Custom revealing exactly two small number
// steppers (target + max, reaching the FULL backend range up to 800) — never
// a form. The react-to-refine control is ONE VoiceTextarea (mic-capable, per
// rule #2) feeding the SAME route.
//
// THE OWNER'S LIVE-TEST FINDING (2026-07-05, on the real Anthropic
// application): the application page ITSELF states the winning length
// ("successful answers are 200-400 words" is Anthropic's own form copy) —
// "which is data we can see." detectWordGuidanceAcross scans the job's
// ALREADY-SERVED description/requirements text (no new fetch, no scraping)
// for a genuine guidance statement; when found, it becomes the DEFAULT
// preset with an honest, verbatim-quoted provenance line. NO INVENTION
// applies to the guidance itself: absence of a real statement never invents
// one — the panel falls back to today's un-derived defaults, no claim made.
//
// HYDRATION (owner's live-test finding #2): the essay is now PERSISTED state,
// not just component state — a page reload (e.g. the Drive-connect OAuth
// round-trip) must not silently lose a previously generated essay. On mount,
// this panel calls GET /jobs/:id/alignment-essay/latest and renders whatever
// it finds immediately, before the user does anything.
//
// THIN PRESENTATION / NO INVENTION: every word here is the server's own
// composed essay body + its own honest provenance line (grounded in your
// DNA + the real source url, or the honest "no verified source" state) —
// this panel invents nothing, it only renders what POST /jobs/:id/
// alignment-essay (or its GET .../latest sibling) served.
// =============================================================================

type Preset = 'detected' | 'short' | 'standard' | 'custom';

const PRESET_WORDS: Record<Exclude<Preset, 'custom' | 'detected'>, { target: number; max: number }> = {
  short: { target: 150, max: 250 },
  standard: { target: 250, max: 400 },
};

/** The backend's own bound (AlignmentEssayService.validateWordBudget:
 *  100 <= target <= max <= 800) — the Custom steppers must reach exactly
 *  this, never a UI-side value narrower than what the server will accept. */
const BACKEND_MAX_WORDS = 800;
const BACKEND_MIN_TARGET_WORDS = 100;

type Phase = 'hydrating' | 'idle' | 'generating' | 'done' | 'error' | 'entitlement_blocked';

export function AlignmentEssayPanel({
  jobId,
  company,
  roleTitle,
  applicantName,
  jobDescription,
  jobRequirements,
  essayCorrections,
  onEssayCorrected,
}: {
  jobId: string;
  /** The served company/job title (JobDetail's own job.company/job.title) —
   *  used to label the panel ("Why {company}?") and to name the print
   *  popup per the house file-naming convention; never sent as an invented
   *  fact — the SERVER independently resolves the company from the job row
   *  it already has. */
  company: string;
  roleTitle?: string | null;
  /** The signed-in user's real displayName (useAuth), passed down from
   *  JobDetail rather than this panel calling useAuth() a second time. */
  applicantName?: string | null;
  /** JobDetail's own job.description — ALREADY served to that page (rendered
   *  verbatim in its own "About this role" card); passed down here so the
   *  word-guidance scan has real text to search WITHOUT a second fetch. Null
   *  when the source never carried a description (an honest absence, never a
   *  reason to fabricate a scan target). */
  jobDescription?: string | null;
  /** JobDetail's own job.requirements — same ALREADY-SERVED posture as
   *  jobDescription; checked as a second scan section (some listings state
   *  the answer-length guidance in a requirements/instructions block rather
   *  than the main description). */
  jobRequirements?: string[] | null;
  /** Phase C/C-P1 — the "that's not right" ledger read for sourceType
   *  'essay_line', owned by JobDetail (same discipline as its own
   *  gapCorrections/strengthCorrections) and passed straight through, keyed
   *  by claimCorrectionKey(jobId, flaggedText). */
  essayCorrections: Record<string, ClaimCorrectionRecord>;
  /** Notifies JobDetail once a correction lands here, so its own
   *  essayCorrections map updates without a full refetch — mirrors the
   *  gap/strengths onCorrected callbacks already wired on that page. */
  onEssayCorrected: (correction: ClaimCorrectionRecord) => void;
}) {
  const [preset, setPreset] = useState<Preset>('standard');
  const [customTarget, setCustomTarget] = useState(250);
  const [customMax, setCustomMax] = useState(400);
  const [phase, setPhase] = useState<Phase>('hydrating');
  const [result, setResult] = useState<AlignmentEssayResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [entitlementBlock, setEntitlementBlock] = useState<{ tier: string; reason: string; hint: string } | null>(
    null,
  );
  const [pastedCulture, setPastedCulture] = useState('');
  const [refineNote, setRefineNote] = useState('');
  // W23's class — set only when a generate "failure" turned out to be a
  // dropped connection over a run the server finished and persisted (see
  // runGenerate's recovery branch). Rendered above the essay so the user
  // knows why the wait looked like it broke.
  const [recoveredNote, setRecoveredNote] = useState<string | null>(null);
  const [copyPhase, setCopyPhase] = useState<'idle' | 'copied' | 'error'>('idle');
  const [printNote, setPrintNote] = useState<string | null>(null);
  // True once the FIRST detected-guidance-driven preset default has been
  // applied — guards against a hydrated essay's own (possibly different)
  // target/max ever being silently overwritten by a LATER-resolving
  // detection memo, and against re-applying the default a second time on an
  // unrelated re-render.
  const [appliedDetectedDefault, setAppliedDetectedDefault] = useState(false);

  // ── WORD-GUIDANCE DETECTION — pure, over already-served text only. See
  // this file's header + lib/detectWordGuidance.ts for the full contract.
  const guidance: WordGuidanceMatch | null = useMemo(
    () => detectWordGuidanceAcross([jobDescription, ...(jobRequirements ?? [])]),
    [jobDescription, jobRequirements],
  );

  useEffect(() => {
    if (!guidance || appliedDetectedDefault) return;
    const derived = deriveBudgetFromMatch(guidance);
    setPreset('detected');
    setCustomTarget(derived.target);
    setCustomMax(derived.max);
    setAppliedDetectedDefault(true);
    // Only on first genuine detection for this job — never re-fires on
    // guidance/appliedDetectedDefault alone changing shape after that (the
    // effect body itself sets appliedDetectedDefault, which is the guard).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guidance]);

  // ── HYDRATION — the panel's own persisted-state read. Runs once per
  // jobId; renders whatever the LATEST non-superseded essay is, or falls
  // through to the honest empty/idle state when none exists yet. A
  // hydrated essay is normalized into the SAME AlignmentEssayResponse shape
  // the generate/refine path already renders — lintGate/blocked/overBudget/
  // grounding stay absent (never invented/replayed; see the GET route's own
  // doc comment for why those specifically are not re-derived).
  useEffect(() => {
    let cancelled = false;
    setPhase('hydrating');
    getLatestAlignmentEssay(jobId)
      .then(({ essay }) => {
        if (cancelled) return;
        if (!essay) {
          setPhase('idle');
          return;
        }
        setResult({
          ok: true,
          essayId: essay.essayId,
          body: essay.body,
          wordCount: essay.wordCount,
          targetWords: essay.targetWords,
          maxWords: essay.maxWords,
          cultureSourceId: essay.cultureSourceId,
          cultureSourceUrl: essay.cultureSourceUrl,
          cultureSourceMissing: essay.cultureSourceMissing,
          cultureSourceProvenance: essay.cultureSourceProvenance,
          revised: false,
          // Phase C/C-P3 — carried straight through from the hydration read;
          // see AlignmentEssayResponse.staleFromCorrection's own doc comment.
          staleFromCorrection: essay.staleFromCorrection,
        });
        // Sync the preset controls to match what actually generated this
        // hydrated essay — Custom/its own numbers, not whatever default
        // preset happened to be selected before hydration resolved. Marks
        // the detected-default effect as already-applied too, so a genuine
        // guidance match on THIS same load never overwrites a real
        // generated essay's own target/max out from under it.
        setCustomTarget(essay.targetWords);
        setCustomMax(essay.maxWords);
        setPreset('custom');
        setAppliedDetectedDefault(true);
        setPhase('done');
      })
      .catch(() => {
        // A hydration failure is never fatal — it just means the panel
        // starts from the honest empty state, same as a job that has never
        // had an essay generated. The user can still generate fresh.
        if (!cancelled) setPhase('idle');
      });
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  const { target, max } =
    preset === 'custom' || preset === 'detected' ? { target: customTarget, max: customMax } : PRESET_WORDS[preset];

  async function runGenerate(args?: { pastedCulture?: string; refineInstructions?: string }) {
    setPhase('generating');
    setError(null);
    setEntitlementBlock(null);
    setRecoveredNote(null);
    // W23's class (docs/WALK-FINDINGS-2026-07-21.md; owner's word
    // `Go-longops`): stamped BEFORE the call so the recovery read below can
    // tell a row THIS attempt produced from one that already existed.
    const attemptStartedAt = Date.now();
    try {
      const r = await generateAlignmentEssay(jobId, {
        targetWords: target,
        maxWords: max,
        pastedCulture: args?.pastedCulture,
        cultureSourceId: result?.cultureSourceId ?? undefined,
        refineInstructions: args?.refineInstructions,
      });
      setResult(r);
      setPhase('done');
      if (args?.refineInstructions) setRefineNote('');
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr?.status === 402 && apiErr.body && typeof apiErr.body === 'object') {
        const body = apiErr.body as { error?: string; tier?: string; reason?: string; hint?: string };
        if (body.error === 'entitlement_required') {
          setPhase('entitlement_blocked');
          setEntitlementBlock({
            tier: body.tier ?? 'free',
            reason: body.reason ?? 'no_subscription',
            hint: body.hint ?? 'start your trial',
          });
          return;
        }
      }
      // ── W23's CLASS on this surface. This is the longest model cascade in
      // the app (R385's ledger row: "the 4-call regen cascade" on Opus), and
      // apiFetch carries no AbortSignal — so a client-side failure here is
      // very often a dropped connection over a run the SERVER finished and
      // safely persisted (alignment_essays is append-only and written before
      // the route answers). Ask the panel's OWN hydration endpoint whether a
      // row landed since this attempt began; if one did, the attempt
      // succeeded and showing "couldn't write that essay" would be a lie
      // about work the user already has. Best-effort: a failed recovery read
      // falls straight through to the honest error below.
      try {
        const { essay } = await getLatestAlignmentEssay(jobId);
        if (essayLandedAfter(essay, attemptStartedAt) && essay) {
          setResult(recoveredEssayResult(essay));
          setCustomTarget(essay.targetWords);
          setCustomMax(essay.maxWords);
          setRecoveredNote(recoveredEssayNote());
          setPhase('done');
          if (args?.refineInstructions) setRefineNote('');
          return;
        }
      } catch {
        // The recovery read itself failed — report the ORIGINAL failure, never
        // a second, more confusing one.
      }
      setPhase('error');
      setError(describeEssayError(err));
    }
  }

  async function handleCopy() {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.body);
      setCopyPhase('copied');
      setTimeout(() => setCopyPhase('idle'), 1600);
    } catch {
      setCopyPhase('error');
    }
  }

  function handlePrint() {
    if (!result) return;
    const documentTitle = buildPrintDocumentTitle({
      displayName: applicantName,
      artifact: 'Alignment Essay',
      roleTitle,
      company,
    });
    const ok = printAlignmentEssay(result.body, applicantName?.trim() || null, documentTitle);
    setPrintNote(ok ? null : 'Couldn’t open the print window — allow pop-ups for this site and try again.');
  }

  const done = phase === 'done' && result != null;
  const blocked = done && result.blocked === true;

  return (
    <Card className={styles.section}>
      <div className={styles.headerRow}>
        <div className={styles.sectionTitle}>Why {company}?</div>
        {done ? <span className={styles.wordChip}>{result.wordCount} words</span> : null}
      </div>

      {/* FIX 1 (owner's live walk, 2026-07-05 — collapse every informational
          blurb by default, keep the ACTION controls — the preset chips/
          Generate button below — visible). This static descriptive line is
          presentation-only (no generation logic touched); reuses the SAME
          native <details>/<summary className={styles.sectionTitle}> idiom
          JobDetail.tsx's own collapsible cards establish, closed by
          default. */}
      <details>
        <summary className={styles.sectionTitle} style={{ cursor: 'pointer' }}>
          What this does
        </summary>
        <p className={styles.introNote} style={{ marginTop: 8 }}>
          A short first-person essay for the "why us" question — weaving what drives you with what{' '}
          {company} actually says it values. Grounded, never generic.
        </p>
      </details>

      {/* THE OWNER'S LIVE-TEST FINDING — when the job's own text states a
          real word-count guidance, say so honestly, verbatim quoted. This
          renders REGARDLESS of which preset is currently active (even after
          the user switches to Short/Standard/Custom) so the provenance for
          why "Detected" appeared at all is never lost. */}
      {guidance ? (
        <div className={styles.provenanceLine}>
          Their listing says: "{guidance.quote}" — I've set that as the target below.
        </div>
      ) : null}

      {/* NO-LONG-FORMS: up to four preset chips (Detected only when the
          job's own text states a real guidance); Custom reveals two small
          number steppers reaching the FULL backend range. */}
      <div className={styles.presetRow} role="group" aria-label="Essay length">
        {guidance ? (
          <button
            type="button"
            className={clsx(styles.presetChip, preset === 'detected' && styles.presetChipActive)}
            onClick={() => {
              const derived = deriveBudgetFromMatch(guidance);
              setCustomTarget(derived.target);
              setCustomMax(derived.max);
              setPreset('detected');
            }}
          >
            Detected {guidance.min !== undefined ? `${guidance.min}-${guidance.max}` : `up to ${guidance.max}`}
          </button>
        ) : null}
        <button
          type="button"
          className={clsx(styles.presetChip, preset === 'short' && styles.presetChipActive)}
          onClick={() => setPreset('short')}
        >
          Short ~150
        </button>
        <button
          type="button"
          className={clsx(styles.presetChip, preset === 'standard' && styles.presetChipActive)}
          onClick={() => setPreset('standard')}
        >
          Standard 250-400
        </button>
        <button
          type="button"
          className={clsx(styles.presetChip, preset === 'custom' && styles.presetChipActive)}
          onClick={() => setPreset('custom')}
        >
          Custom
        </button>
      </div>

      {preset === 'custom' || preset === 'detected' ? (
        <div className={styles.stepperRow}>
          <label className={styles.stepperField}>
            Target
            <div className={styles.stepper}>
              <button type="button" onClick={() => setCustomTarget((v) => Math.max(BACKEND_MIN_TARGET_WORDS, v - 25))}>
                −
              </button>
              <span>{customTarget}</span>
              <button type="button" onClick={() => setCustomTarget((v) => Math.min(customMax, v + 25))}>
                +
              </button>
            </div>
          </label>
          <label className={styles.stepperField}>
            Max
            <div className={styles.stepper}>
              <button type="button" onClick={() => setCustomMax((v) => Math.max(customTarget, v - 25))}>
                −
              </button>
              <span>{customMax}</span>
              <button type="button" onClick={() => setCustomMax((v) => Math.min(BACKEND_MAX_WORDS, v + 25))}>
                +
              </button>
            </div>
          </label>
        </div>
      ) : null}

      {/* THE OWNER'S LIVE-TEST FEEDBACK ("we need that to be more than 400
          if we want to") — the Custom Max stepper already reached 800 via
          Math.min above, but nothing in the UI ever SAID so; a user pressing
          + a few times from the 400 default with no visible ceiling had no
          way to tell 800 was reachable at all versus silently capped at
          whatever number they stopped clicking on. This makes the true,
          backend-matching ceiling discoverable without brute-force clicking. */}
      {preset === 'custom' || preset === 'detected' ? (
        <div className={styles.toolbarNote}>Max goes up to {BACKEND_MAX_WORDS} words.</div>
      ) : null}

      {/* HYDRATION — checking for a previously generated, persisted essay
          before showing the empty-generator state. Quiet and brief (a
          single DB read); distinct copy from 'generating' so it never reads
          as "writing a new essay" when we're only checking for an existing
          one. */}
      {phase === 'hydrating' ? (
        <div className={styles.generatingRow}>
          <span className={styles.spinner} />
          Checking for an essay you've already started…
        </div>
      ) : null}

      {phase === 'idle' ? (
        <button type="button" className={styles.generateButton} onClick={() => runGenerate()}>
          Write my "Why {company}" essay
        </button>
      ) : null}

      {phase === 'generating' ? (
        <div className={styles.generatingRow}>
          <span className={styles.spinner} />
          Weaving your story into theirs…
        </div>
      ) : null}

      {/* W23's class — the attempt looked like it failed on this device, but
          the run finished server-side and the essay below IS that result.
          A note, never an error: nothing failed except the connection. */}
      {recoveredNote ? <div className={styles.toolbarNote}>{recoveredNote}</div> : null}

      {phase === 'error' && error ? (
        <div className={styles.errorWrap}>
          <div className={styles.errorNote} role="alert">
            {error}
          </div>
          <button type="button" className={styles.generateButton} onClick={() => runGenerate()}>
            Try again
          </button>
        </div>
      ) : null}

      {phase === 'entitlement_blocked' && entitlementBlock ? (
        <div className={styles.advisoryBox} role="status">
          <div className={styles.advisoryTitle}>{describeEntitlementReason(entitlementBlock.reason)}</div>
          <div className={styles.introNote}>
            {entitlementBlock.hint === 'start your trial'
              ? 'Start your free trial to write this essay — I\'ll weave in your real story.'
              : entitlementBlock.hint}
          </div>
        </div>
      ) : null}

      {done ? (
        <div className={styles.doneWrap}>
          {blocked && result.lintGate ? (
            <div className={styles.blockedBox} role="alert">
              <div className={styles.blockedTitle}>
                Held back — {result.lintGate.blockingCount} bit{result.lintGate.blockingCount === 1 ? '' : 's'}{' '}
                needed another pass before I'd show it
              </div>
              {result.lintGate.blocks
                .filter((b) => !b.passed)
                .map((block) => (
                  <div key={block.location} className={styles.blockedBlock}>
                    {block.diffs.map((diff, i) => (
                      <div key={i} className={styles.blockedDiff}>
                        {diff.span ? <span className={styles.blockedSpan}>"{diff.span}"</span> : null}
                        <span> — {diff.suggestion}</span>
                      </div>
                    ))}
                  </div>
                ))}
            </div>
          ) : (
            <>
              {/* Phase C/C-P3 (staleness fan-out) — a SIMPLE timestamp-derived
                  flag from the server (never a stored one; see the route's
                  own doc comment), offered here, never auto-fired: the SAME
                  "Regenerate" the react-to-refine loop already uses below,
                  just surfaced with an honest reason. NO auto-regeneration —
                  human-in-the-loop law. */}
              {result.staleFromCorrection ? (
                <div className={styles.advisoryBox} role="status">
                  <div className={styles.advisoryTitle}>
                    A correction touches this essay — regenerate to pick it up?
                  </div>
                  <button
                    type="button"
                    className={styles.secondaryButton}
                    disabled={(phase as Phase) === 'generating'}
                    onClick={() => runGenerate()}
                  >
                    Regenerate
                  </button>
                </div>
              ) : null}

              {result.overBudget ? (
                <div className={styles.advisoryBox} role="status">
                  <div className={styles.advisoryTitle}>
                    Still over {result.maxWords} words after one tightening pass — {result.wordCount} words
                    shown below. Try "Custom" with a higher max, or ask me to cut more via the note below.
                  </div>
                </div>
              ) : null}

              {/* THE FORWARD-LOOKING LAW's own honest flag (docs/design/
                  alignment-essay-module-2026-07-05.md's tail section) — never
                  blocks the render (the essay below is still yours to use),
                  same advisory posture as the overBudget box above. Only
                  shown on a fresh generation (openingStyle is a per-
                  generation-run verdict, absent on a hydrated read — see
                  AlignmentEssayResponse.openingStyle's own doc comment). */}
              {result.openingStyle === 'backward' ? (
                <div className={styles.advisoryBox} role="status">
                  <div className={styles.advisoryTitle}>
                    This still opens looking backward, even after one rewrite — try "Tell me what to
                    change" below (e.g. "open on where this is headed, not how I got here").
                  </div>
                </div>
              ) : null}

              {result.cultureSourceMissing ? (
                <div className={styles.advisoryBox} role="status">
                  <div className={styles.advisoryTitle}>
                    I couldn't verify {company}'s published values — this draft is grounded in your story
                    only. Paste their values/careers page and I'll regenerate with it woven in.
                  </div>
                  <VoiceTextarea
                    label="Paste their values or careers page text"
                    value={pastedCulture}
                    onChange={setPastedCulture}
                    rows={4}
                    placeholder="Paste the text from their careers or values page here…"
                  />
                  <button
                    type="button"
                    className={styles.secondaryButton}
                    disabled={!pastedCulture.trim() || phase === ('generating' as Phase)}
                    onClick={() => runGenerate({ pastedCulture })}
                  >
                    Regenerate with their values
                  </button>
                </div>
              ) : (
                <div className={styles.provenanceLine}>
                  {/* The server's own ready-made sentence (worded per
                      sourceKind ONE place, backend-side — see
                      AlignmentEssayService.formatCultureProvenanceLine) —
                      preferred over re-deriving a generic line here, so a
                      new sourceKind's phrasing can never drift out of sync
                      between server and client. Falls back to the
                      pre-existing generic phrasing only for an older
                      persisted essay row generated before this field
                      existed. */}
                  {result.cultureSourceProvenance ??
                    `Grounded in your DNA + ${result.cultureSourceUrl ?? 'their pasted values text'}`}
                </div>
              )}

              {/* GATE-2 (the verdict renders, Phase B) — the grounding gate's
                  advisory/hard flags, same posture + copy as JobDetail.tsx's
                  own cover-letter groundingFlagBox (R345's honesty pass: the
                  API has carried `result.grounding` since before this lane,
                  but nothing here ever rendered it — landed-but-strangled,
                  the same class R332 fixed for bullshitReview). Present only
                  when the server actually flagged something notable. */}
              {result.grounding && result.grounding.flags.length > 0 ? (
                <div className={styles.groundingFlagBox} role="status">
                  <div className={styles.groundingFlagTitle}>Flagged for review before you send</div>
                  {result.grounding.flags.map((flag, i) => (
                    <div key={i} className={styles.groundingFlagLine}>
                      {flag.reason === 'entity_mismatch'
                        ? `A number ("${flag.value}") looks real, but not at ${flag.entity ?? 'the company named there'} — take a look before you send.`
                        : flag.reason === 'user_corrected'
                          ? `This resurfaces something you already told me wasn't right${flag.correctedClaim ? `: "${flag.correctedClaim}"` : ''}.`
                          : flag.reason === 'ungrounded_figure'
                            ? `A number ("${flag.value}") didn't clearly match anything you've told me — take a look before you send.`
                            : `A line didn't trace cleanly to your story: "${flag.value}"`}
                      {/* Phase C/C-P1 — the "that's not right" affordance,
                          per flagged claim. flaggedText is the flag's own
                          `value` (a bare figure for entity_mismatch/
                          ungrounded_figure, the full sentence for
                          ungrounded_narrative/user_corrected) — thinner than
                          the full sentence for figure-shaped flags, but a
                          real, honest excerpt of what was actually flagged;
                          the whole-essay control below covers anything this
                          per-flag grain doesn't. */}
                      <ClaimCorrectionControl
                        sourceType="essay_line"
                        sourceId={jobId}
                        flaggedText={flag.value}
                        existingCorrection={essayCorrections[claimCorrectionKey(jobId, flag.value)]}
                        onCorrected={onEssayCorrected}
                      />
                    </div>
                  ))}
                </div>
              ) : null}

              <div className={styles.essayPreview}>
                <div className={styles.essayHeader}>
                  <div className={styles.sectionTitle}>Essay — ready to send</div>
                  {/* W16 (docs/WALK-FINDINGS-2026-07-21.md), his spec verbatim:
                      "we still need a word count on this" — result.wordCount
                      was already served (AlignmentEssayService's own
                      analyzeText/BrevityService pass over the real body,
                      headerRow above already renders it) but only up in the
                      card's OUTER header, scrolled well past the preset
                      chips/provenance/grounding blocks by the time the essay
                      text itself is on screen — exactly where he was looking
                      when he said this. Same served numbers, surfaced again
                      right here: the real count plus the real requested
                      budget (targetWords/maxWords — whatever preset/custom
                      values actually generated THIS essay), never a re-derived
                      or invented target. */}
                  <div className={styles.essayWordMeta}>
                    <span className={styles.wordChip}>{result.wordCount} words</span>
                    <span className={styles.essayBudgetNote}>
                      target {result.targetWords}-{result.maxWords}
                    </span>
                  </div>
                  <div className={styles.essayActions}>
                    <button type="button" className={styles.toolButton} onClick={handleCopy}>
                      {copyPhase === 'copied' ? 'Copied' : copyPhase === 'error' ? "Couldn't copy" : 'Copy'}
                    </button>
                    <button type="button" className={styles.toolButton} onClick={handlePrint}>
                      Print / Save as PDF
                    </button>
                    {/* GAP-CLOSE (2026-07-05) — the essay's own Save-to-Drive
                        affordance, same conditional-on-a-real-id mount as
                        JobResumePanel.tsx/JobDetail.tsx's cover-letter card:
                        only renders once a real essayId exists (a run that
                        never generated has nothing to save). */}
                    {result.essayId ? (
                      <DriveSaveButton artifactType="alignment_essay" artifactId={result.essayId} />
                    ) : null}
                  </div>
                </div>
                {printNote ? <div className={styles.toolbarNote}>{printNote}</div> : null}
                <div className={styles.essayBody}>{result.body}</div>
                {/* Phase C/C-P1 — the whole-essay catch-all "that's not
                    right", for anything the gate itself didn't flag (Jason
                    catching a wrong detail the trace missed entirely, or a
                    nuance only he knows — the Kaiser/Quotient case this
                    whole loop exists for). truncateForCorrection keeps
                    flaggedText under the corrections route's own zod cap
                    (4000 chars) — an essay can run up to 800 words (~4800+
                    chars); a real, honest excerpt (never fabricated text),
                    just not the full body — his own note carries the rest. */}
                {(() => {
                  const wholeEssayFlagText = truncateForCorrection(result.body);
                  return (
                    <ClaimCorrectionControl
                      sourceType="essay_line"
                      sourceId={jobId}
                      flaggedText={wholeEssayFlagText}
                      existingCorrection={essayCorrections[claimCorrectionKey(jobId, wholeEssayFlagText)]}
                      onCorrected={onEssayCorrected}
                    />
                  );
                })()}
              </div>

              {/* THE READER'S CHAIR (docs/design/bullshit-detector-2026-07-
                  05.md) — the hiring-manager-voiced adversarial review.
                  Present only when the FINAL detect pass still has findings
                  (the API only sends this field when non-empty); never
                  blocks the render above. THIN PRESENTATION: every quote/
                  category/reaction here is rendered EXACTLY as the server
                  computed it — no client-side judgment, no re-derivation. */}
              {result.bullshitReview ? (
                <div className={styles.readersChairBox} role="status">
                  <div className={styles.readersChairVerdict}>
                    {result.bullshitReview.revised
                      ? 'Reader’s chair, after one rewrite: '
                      : 'Reader’s chair: '}
                    {result.bullshitReview.overallVerdict}
                  </div>
                  {result.bullshitReview.findings.map((finding, i) => (
                    <div key={i} className={styles.readersChairFinding}>
                      <span className={styles.readersChairChip}>{describeBullshitCategory(finding.category)}</span>
                      <div className={styles.readersChairQuote}>&ldquo;{finding.quote}&rdquo;</div>
                      <div className={styles.readersChairReaction}>{finding.reaction}</div>
                    </div>
                  ))}
                </div>
              ) : null}

              {/* NO-ONE-SHOT-WONDERS — the react-to-refine loop, re-entering
                  the SAME route with refineInstructions. */}
              <div className={styles.refineRow}>
                <VoiceTextarea
                  label="Tell me what to change"
                  value={refineNote}
                  onChange={setRefineNote}
                  rows={2}
                  placeholder="e.g. lead with the mentoring story, not the metrics…"
                />
                <button
                  type="button"
                  className={styles.secondaryButton}
                  disabled={!refineNote.trim() || (phase as Phase) === 'generating'}
                  onClick={() => runGenerate({ refineInstructions: refineNote })}
                >
                  Regenerate with this change
                </button>
              </div>
            </>
          )}
        </div>
      ) : null}
    </Card>
  );
}

/** Phase C/C-P1 — a verbatim (never rewritten) excerpt safely under the
 *  claim-corrections route's own zod cap (flaggedText max 4000 chars),
 *  for the whole-essay "that's not right" control. 500 is comfortably
 *  under that cap even after the "…" suffix, while still being a real,
 *  readable chunk of what the person is actually disputing. */
function truncateForCorrection(text: string): string {
  return text.length > 500 ? `${text.slice(0, 500)}…` : text;
}

function describeEssayError(err: unknown): string {
  const apiErr = err as ApiError;
  const body = apiErr?.body as { error?: string; reason?: string } | undefined;
  if (body?.error === 'no_anthropic_key') {
    return "This isn't ready yet on my end — try again in a bit.";
  }
  if (body?.error === 'invalid_word_budget') {
    if (body.reason === 'target_too_low') return 'Target length needs to be at least 100 words.';
    if (body.reason === 'target_above_max') return "Target can't be higher than the max — adjust one of them.";
    if (body.reason === 'max_too_high') return 'Max length caps at 800 words.';
    return "Those lengths don't work together — adjust target/max.";
  }
  return err instanceof Error ? err.message : 'Could not write this essay — try again.';
}

/** Human-facing label for a reader's-chair finding's category chip — worded
 *  HERE, once, client-side (this is presentation-only labeling of a server-
 *  computed enum, not a judgment call — mirrors describeEntitlementReason's
 *  own "map a server enum to display copy" shape immediately below). */
// Exported (2026-07-08): JobDetail's cover-letter Reader's-chair block reuses
// the SAME wording — one vocabulary for the reviewer across surfaces.
export function describeBullshitCategory(category: BullshitFinding['category']): string {
  switch (category) {
    case 'slop':
      return 'AI slop';
    case 'smoke':
      return 'Smoke';
    case 'buried_lede':
      return 'Buried lede';
    case 'generic':
      return 'Generic';
    case 'unsupported_claim':
      return 'Unsupported claim';
    default:
      return 'Flagged';
  }
}

function describeEntitlementReason(reason: string): string {
  switch (reason) {
    case 'trial_expired':
      return "Your free trial's wrapped up.";
    case 'past_due_lapsed':
      return 'Your last payment didn’t go through, and the grace period’s run out too.';
    case 'canceled':
      return 'Your subscription’s been canceled.';
    default:
      return "Writing this essay is a paid feature — want to start a trial?";
  }
}
